import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, Checkbox, FormControlLabel, Chip, Divider
} from '@mui/material';
import { crearTramiteReconocimiento, getChecklistFormularios, getTramitesReconocimiento } from '../../services/reconocimientoService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';

const SUBTIPOS = [
    { value: 'COMUN', label: 'Común' },
    { value: 'SERVICIO_DOMESTICO', label: 'Servicio Doméstico' },
];

/**
 * Formulario para iniciar un trámite de Reconocimiento de Servicios. A diferencia de PUAM, tiene
 * 2 subtipos con checklist distinto (patrón Jubilación): el checklist se recalcula en el backend
 * (función pura) cada vez que cambia el subtipo o tieneRelacionEmpleador.
 *
 * El reconocimiento no se crea suelto: se inicia SOLO desde la ficha de un trámite padre
 * (Jubilación o Plan de Pago), embebido en un modal. Ahí se le pasan `idTramitePadre` (se manda
 * en el alta, vínculo uno-a-muchos) y `cuilFijo` (el cliente viene del padre, no se busca ni se
 * puede cambiar). Como un mismo padre puede tener varios reconocimientos (ej. doméstico: se
 * acredita un período, después se consiguen más recibos y se hace otro), el chequeo de
 * duplicados por CUIL no aplica en este flujo.
 *
 * También se abre desde la ficha de atención de recepción, como el resto de los módulos: ahí llegan
 * `cuilInicial` (el cliente del turno, se puede cambiar) e `idTurnoOrigen`, que se manda en el alta
 * para que el trámite cuente en la conversión de esa atención.
 */
export default function FormularioIniciarTramiteReconocimiento({
    onSuccess, onCancel, idTramitePadre = null, cuilFijo = null, cuilInicial = null, idTurnoOrigen = null,
}) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const clienteBloqueado = !!cuilFijo;
    const embebido = !!idTramitePadre;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        organismo: '',
        subtipo: 'COMUN',
        detalleReconocimiento: '',
        tieneRelacionEmpleador: false,
        comentarios: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Reconocimiento duplicado para el mismo cliente (patrón RTI/ART/Jubilación/PUAM) ---
    const [tieneDuplicado, setTieneDuplicado] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Checklist (condicional por subtipo/tieneRelacionEmpleador: preview en vivo) ---
    const [checklist, setChecklist] = useState([]);
    const [cargandoChecklist, setCargandoChecklist] = useState(true);

    // Carga inicial de cliente: fijo (embebido desde un padre), el del turno (ficha de atención) o
    // por URL (uso standalone).
    useEffect(() => {
        const cuilACargar = cuilFijo || cuilInicial || cuilParam;
        if (!cuilACargar) return;
        const cargar = async () => {
            try {
                const cliente = await getClientePorCuil(cuilACargar);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilFijo, cuilInicial, cuilParam]);

    // Preview del checklist: se recalcula en el backend (función pura) según subtipo/flag.
    useEffect(() => {
        const cargarChecklist = async () => {
            setCargandoChecklist(true);
            try {
                const data = await getChecklistFormularios({
                    subtipo: form.subtipo,
                    tieneRelacionEmpleador: form.subtipo === 'SERVICIO_DOMESTICO' && form.tieneRelacionEmpleador,
                });
                setChecklist(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error al previsualizar checklist:', err);
                setChecklist([]);
            } finally {
                setCargandoChecklist(false);
            }
        };
        cargarChecklist();
    }, [form.subtipo, form.tieneRelacionEmpleador]);

    // Trámite de Reconocimiento ya existente para el cliente seleccionado (patrón RTI/ART/Jubilación/PUAM).
    // No tiene estados de cierre, así que "activo" = existe cualquier trámite con ese CUIL.
    // No aplica cuando viene embebido desde un padre: ahí varios reconocimientos son lo esperado.
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente || idTramitePadre) {
            setTieneDuplicado(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesReconocimiento(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicado(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de Reconocimiento duplicados:', err);
                if (activo) {
                    setTieneDuplicado(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };

        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente, idTramitePadre]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value, ...(name === 'subtipo' && value !== 'SERVICIO_DOMESTICO' ? { tieneRelacionEmpleador: false } : {}) }));
    };

    const handleCheck = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (tieneDuplicado && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de Reconocimiento de Servicios para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                idTramitePadre: idTramitePadre || null,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
                organismo: form.organismo || null,
                subtipo: form.subtipo,
                detalleReconocimiento: form.detalleReconocimiento,
                tieneRelacionEmpleador: form.subtipo === 'SERVICIO_DOMESTICO' ? form.tieneRelacionEmpleador : false,
                comentarios: form.comentarios,
            };
            const creado = await crearTramiteReconocimiento(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de Reconocimiento de Servicios creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/reconocimiento/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={embebido
                ? { background: 'transparent', padding: 0 }
                : { background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: embebido ? '12px' : '16px', boxShadow: embebido ? 'none' : 'var(--shadow-md)', overflow: 'hidden' }}>
                    {/* Header */}
                    {!embebido && (
                        <PageHeader
                            variante="banner"
                            icono="folder2-open"
                            titulo="Iniciar Trámite de Reconocimiento de Servicios"
                            descripcion="Complete los datos para registrar un nuevo trámite previo a la jubilación"
                        />
                    )}

                    <Box sx={{ padding: embebido ? 0 : '2rem' }}>
                    {!clienteBloqueado && (
                        <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />
                    )}

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {!clienteBloqueado && (
                                <>
                                    {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <Autocomplete
                                            id="buscar-cliente-reconocimiento"
                                            options={opcionesCliente}
                                            getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                            onInputChange={handleBusquedaCliente}
                                            onChange={handleSeleccionCliente}
                                            value={clienteSeleccionado}
                                            isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                            noOptionsText="No se encontraron clientes"
                                            loading={buscando}
                                            loadingText="Buscando..."
                                            sx={{ flexGrow: 1 }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Buscar cliente por CUIL, Apellido o Nombre"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                        <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                            Registrar Cliente
                                        </Button>
                                    </Box>
                                </>
                            )}
                            {clienteBloqueado && (
                                <Alert severity="info" icon={<i className="bi bi-person-check" />} sx={{ borderRadius: '10px' }}>
                                    Cliente: <strong>{clienteSeleccionado ? `${apellidoNombre(clienteSeleccionado.apellido, clienteSeleccionado.nombre)} (${clienteSeleccionado.cuil})` : cuilFijo}</strong>
                                    {' '}— viene del trámite padre, no se puede cambiar.
                                </Alert>
                            )}

                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {/* Datos del trámite */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Trámite')}

                            <TextField select label="Subtipo" name="subtipo" value={form.subtipo} onChange={handleChange} fullWidth required
                                helperText="Reconocimiento de servicios comunes o de servicio doméstico (casas particulares).">
                                {SUBTIPOS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                            </TextField>

                            <TextField select label="Organismo" name="organismo" value={form.organismo} onChange={handleChange} fullWidth
                                helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES.">
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="ANSES">ANSES</MenuItem>
                                <MenuItem value="PROVINCIA">Provincia</MenuItem>
                            </TextField>

                            <TextField label="Detalle de lo que se reconoce" name="detalleReconocimiento" value={form.detalleReconocimiento} onChange={handleChange} fullWidth
                                helperText="Ej: período y empleador/actividad a reconocer." />

                            {form.subtipo === 'SERVICIO_DOMESTICO' && (
                                <FormControlLabel
                                    control={<Checkbox name="tieneRelacionEmpleador" checked={form.tieneRelacionEmpleador} onChange={handleCheck} />}
                                    label="Tiene relación con el empleador (contacto vivo) — si no, se pide PS 2.94 en su lugar"
                                />
                            )}

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Observaciones libres sobre el trámite." />

                            {/* Checklist (condicional por subtipo/relación con el empleador) */}
                            <Box sx={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="#1e40af" sx={{ mb: 1 }}>
                                    <i className="bi bi-check2-square me-2"></i>
                                    Formularios requeridos {cargandoChecklist && <CircularProgress size={14} sx={{ ml: 1 }} />}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                {checklist.length === 0 && !cargandoChecklist ? (
                                    <Typography variant="body2" color="text.secondary">Sin formularios configurados.</Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {checklist.map(f => <Chip key={f.codigo} label={f.nombre} size="small" color="primary" variant="outlined" />)}
                                    </Stack>
                                )}
                            </Box>

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicado && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Reconocimiento de Servicios' : `${cantidadDuplicados} trámites de Reconocimiento de Servicios`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicado && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label="Confirmo que deseo continuar con un nuevo trámite de Reconocimiento de Servicios igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Reconocimiento activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
